// API client for SkeeScore backend

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://cwveact253.execute-api.us-east-1.amazonaws.com';

export interface CreateGameRequest {
  detectionConfig?: {
    motionThreshold?: number;
    minBlobArea?: number;
    maxBlobArea?: number;
  };
}

export interface CreateGameResponse {
  gameId: string;
  createdAt: number;
  status: string;
  totalScore: number;
  ballCount: number;
}

export interface SubmitEventRequest {
  timestamp: number;
  ballId: number;
  holeValue: number;
  confidence: number;
  position: {
    x: number;
    y: number;
  };
}

export interface SubmitEventResponse {
  eventId: string;
  totalScore: number;
}

export interface GameEvent {
  eventId: string;
  gameId: string;
  timestamp: number;
  ballId: number;
  holeValue: number;
  confidence: number;
  position: {
    x: number;
    y: number;
  };
  // Fields for tracking manual edits (for ML training)
  originalValue?: number;     // Original camera-detected score
  isManualEdit?: boolean;     // Flag indicating manual edit (from backend)
  isEdited?: boolean;         // Flag indicating manual edit (local state)
  editedAt?: number;          // Timestamp when edited
}

export interface Game {
  gameId: string;
  createdAt: number;
  updatedAt: number;
  status: string;
  totalScore: number;
  ballCount: number;
  detectionConfig?: {
    motionThreshold?: number;
    minBlobArea?: number;
    maxBlobArea?: number;
  };
}

export interface GetGameResponse {
  game: Game;
  events: GameEvent[];
}

export interface ListGamesResponse {
  games: Game[];
  lastEvaluatedKey?: string;
}

export interface UploadLogsRequest {
  logs: LogEntry[];
}

export interface UpdateEventRequest {
  holeValue: number;
  notes?: string;
}

export interface UpdateEventResponse {
  event: GameEvent;
  newTotalScore: number;
}

export interface UpdateEventRequest {
  holeValue: number;
}

export interface LogEntry {
  timestamp: number;
  level: 'log' | 'warn' | 'error' | 'info';
  message: string;
}

export interface GetLogsResponse {
  url: string;
  expiresIn: number;
}

export interface LogData {
  gameId: string;
  logType: string;
  uploadedAt: number;
  logs: LogEntry[];
}

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async createGame(request: CreateGameRequest = {}): Promise<CreateGameResponse> {
    const response = await fetch(`${this.baseUrl}/games`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to create game: ${response.statusText}`);
    }

    return response.json();
  }

  async submitEvent(gameId: string, event: SubmitEventRequest): Promise<SubmitEventResponse> {
    const response = await fetch(`${this.baseUrl}/games/${gameId}/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      throw new Error(`Failed to submit event: ${response.statusText}`);
    }

    return response.json();
  }

  async getGame(gameId: string): Promise<GetGameResponse> {
    const response = await fetch(`${this.baseUrl}/games/${gameId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to get game: ${response.statusText}`);
    }

    return response.json();
  }

  async listGames(limit: number = 20, status?: string): Promise<ListGamesResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (status) {
      params.append('status', status);
    }

    const response = await fetch(`${this.baseUrl}/games?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to list games: ${response.statusText}`);
    }

    return response.json();
  }

  async uploadLogs(gameId: string, logs: UploadLogsRequest): Promise<void> {
    const response = await fetch(`${this.baseUrl}/games/${gameId}/logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(logs),
    });

    if (!response.ok) {
      throw new Error(`Failed to upload logs: ${response.statusText}`);
    }
  }

  async updateEvent(gameId: string, eventId: string, update: UpdateEventRequest): Promise<UpdateEventResponse> {
    // URL-encode the eventId to handle special characters like # in "timestamp#ballId"
    const encodedEventId = encodeURIComponent(eventId);

    const response = await fetch(`${this.baseUrl}/games/${gameId}/events/${encodedEventId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(update),
    });

    if (!response.ok) {
      throw new Error(`Failed to update event: ${response.statusText}`);
    }

    return response.json();
  }

  async getLogDownloadUrl(gameId: string, logType: 'browser' | 'detection' = 'browser'): Promise<GetLogsResponse> {
    const response = await fetch(`${this.baseUrl}/games/${gameId}/logs?logType=${logType}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to get log download URL: ${response.statusText}`);
    }

    return response.json();
  }

  async getLogs(gameId: string, logType: 'browser' | 'detection' = 'browser'): Promise<LogData | null> {
    try {
      // First, get the presigned URL
      const urlResponse = await this.getLogDownloadUrl(gameId, logType);

      // Then fetch the actual log data from S3
      const logsResponse = await fetch(urlResponse.url);

      if (!logsResponse.ok) {
        console.warn(`No ${logType} logs found for game ${gameId}`);
        return null;
      }

      return logsResponse.json();
    } catch (error) {
      console.warn(`Failed to fetch ${logType} logs for game ${gameId}:`, error);
      return null;
    }
  }

  async deleteGame(gameId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/games/${gameId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete game: ${response.statusText}`);
    }
  }
}

export const apiClient = new ApiClient();

